import '@app/App.css';
import { Button } from "@comps/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@comps/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@comps/ui/form";
import { Input } from "@comps/ui/input";
import { CompozzError, isCompozzError } from "@dto/CompozzError";
import { ObjectDto } from "@dto/objects";
import { FullRightAccess, hasGlobalRightAccess, ViewRightType } from "@dto/Users";
import { View } from "@dto/views";
import { zodResolver } from "@hookform/resolvers/zod";
import { BASE_URL } from "@utils/constants";
import { getWsButtonStyle, TW_AppDialogButton, TW_ButtonList, TW_ErrorMessage, TW_ViewMenuItem, TW_ViewMenuItemIcon, useHover } from "@utils/twClasses";
import { put } from "@utils/utils";
import { Edit2 } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { translate } from "src/i18n/i18n";
import { useModelStore, useUserStore } from "src/store";
import { z } from "zod";

interface ViewMenuRenameItemProps {
  object: ObjectDto;
  view: View;
  onViewUpdate: (view: View) => void;
  closeMenu: () => void;
}

function ViewMenuRenameItem({ object, view, onViewUpdate, closeMenu }: ViewMenuRenameItemProps) {

  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const wsHoverColor = useModelStore(state => state.wsHoverColor);
  const buttonHover = useHover(getWsButtonStyle('transparent'), getWsButtonStyle(wsHoverColor));  
  const user = useUserStore(state => state.user);
  const userRole = useModelStore(state => state.userRole);

  const hasFullViewAccess = view.ownerKey === user?._key || hasGlobalRightAccess(user, userRole, ViewRightType, FullRightAccess, view._key);

  const viewUpdated = (view: View) => {
    setIsDialogOpen(false);
    closeMenu();
    onViewUpdate(view);
  }

  function cancel(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    setIsDialogOpen(false);
    closeMenu();
  }

  function click(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    if (!hasFullViewAccess) {
      return;
    }
    setIsDialogOpen(true);
  }

  function onOpenChange(open: boolean) {
    setIsDialogOpen(open);
    if (!open) {
      closeMenu();
    }
  }


  const disabled = view === null || !hasFullViewAccess;
  return (
    <Dialog open={isDialogOpen} onOpenChange={onOpenChange}>
      <DialogTrigger className={TW_ViewMenuItem + ` ${disabled ? "item-disabled" : ""}`} {...buttonHover} onClick={click}><Edit2 className={TW_ViewMenuItemIcon} />{t("view.button.rename.text")}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("view.dialog.rename.title")}</DialogTitle>
          <DialogDescription className="hidden">{t("object.dialog.rename.description")}</DialogDescription>
        </DialogHeader>
        {view && <ViewRenameForm object={object} initialView={view} onViewUpdate={viewUpdated} onCancel={cancel} />}
      </DialogContent>
    </Dialog>
  )
}

interface ViewRenameFormProps {
  object: ObjectDto;
  initialView: View;
  onViewUpdate: (view: View) => void;
  onCancel: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

function ViewRenameForm({ object, initialView, onViewUpdate, onCancel }: ViewRenameFormProps) {

  const { t } = useTranslation();
  const workspace = useModelStore(state => state.selectedWorkspace);
  const [error, setError] = useState<string | null>(null);
  const wsColor = useModelStore(state => state.wsColor);
  const wsDarkColor = useModelStore(state => state.wsDarkColor);
  const cancelHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));
  const okHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));

  const formSchema = z.object({
    viewName: z.string().min(1, {
      message: t("view.dialog.error.name.missing"),
    }),
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      viewName: initialView.name,
    },
  })

  function onSubmit(values: z.infer<typeof formSchema>) {

    if (!workspace?._key) {
      console.error("Workspace key is not set");
      return;
    }

    // No need to update the workspace if the name is the same
    if (values.viewName === initialView.name) {
      return;
    }

    const updatedView = {
      ...initialView,
      name: values.viewName,
    }
    put<View>(`${BASE_URL}/model/workspaces/${workspace._key}/objects/${object._key}/views`, updatedView).then(response => {
      if (response) {
        if (isCompozzError(response)) {
          const cError = response as CompozzError;
          if (cError.i18n) {
            setError(translate(t, cError.i18n, cError.params));
          } else {
            setError(cError.message);
          }
        }
      } else {
        setError(null);
        onViewUpdate(updatedView);
      }
    })
  }

  function onEnterKeyPress(event: React.KeyboardEvent) {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      form.handleSubmit(onSubmit)();
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <FormField
          control={form.control}
          name="viewName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("view.dialog.rename.label")}</FormLabel>
              <FormControl>
                <Input placeholder={t("view.dialog.rename.placeholder")} {...field} onKeyDown={onEnterKeyPress} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {error && <div className={TW_ErrorMessage}>{error}</div>}
        {!error && <div className={TW_ErrorMessage}></div>}
        <div className={TW_ButtonList}>
          <Button className={TW_AppDialogButton} onClick={onCancel} {...cancelHover}>{t("general.button.cancel.text")}</Button>
          <Button className={TW_AppDialogButton} type="submit" {...okHover}>{t("general.button.confirm.text")}</Button>
        </div>
      </form>
    </Form>
  )
}

export default ViewMenuRenameItem;
